import { identity } from './function.no-deps'
import { isNil } from './nil.no-deps'

/**
 * Like `pipe`, except it will bail early if the piped value is `Nil`
 * Where `Nil` means `null | undefined`
 *
 *    nilPipe(
 *        riskyFunction(),
 *        nullUnless(isSuccess),
 *        r => r.value + ' You!'
 *    )
 */
export function nilPipe<T1>(value: T1): T1
export function nilPipe<T1, T2>(value: T1, f1: (t: T1) => T2): T2
export function nilPipe<T1, T2, T3>(value: T1, f1: (t: T1) => T2, f2: (t: T2) => T3): T3
export function nilPipe<T1, T2, T3, T4>(value: T1, f1: (t: T1) => T2, f2: (t: T2) => T3, f3: (t: T3) => T4): T4

export function nilPipe<T1, T2, T3, T4, T5>(
    value: T1,
    f1: (t: T1) => T2,
    f2: (t: T2) => T3,
    f3: (t: T3) => T4,
    f4: (t: T4) => T5
): T5

export function nilPipe<T1, T2, T3, T4, T5, T6>(
    value: T1,
    f1: (t: T1) => T2,
    f2: (t: T2) => T3,
    f3: (t: T3) => T4,
    f4: (t: T4) => T5,
    f5: (t: T5) => T6
): T6

export function nilPipe<T1, T2, T3, T4, T5, T6, T7>(
    value: T1,
    f1: (t: T1) => T2,
    f2: (t: T2) => T3,
    f3: (t: T3) => T4,
    f4: (t: T4) => T5,
    f5: (t: T5) => T6,
    f6: (t: T6) => T7
): T7

export function nilPipe<T1, T2, T3, T4, T5, T6, T7, T8>(
    value: T1,
    f1: (t: T1) => T2,
    f2: (t: T2) => T3,
    f3: (t: T3) => T4,
    f4: (t: T4) => T5,
    f5: (t: T5) => T6,
    f6: (t: T6) => T7,
    f7: (t: T7) => T8
): T8

export function nilPipe<T1, T2, T3, T4, T5, T6, T7, T8, T9>(
    value: T1,
    f1: (t: T1) => T2,
    f2: (t: T2) => T3,
    f3: (t: T3) => T4,
    f4: (t: T4) => T5,
    f5: (t: T5) => T6,
    f6: (t: T6) => T7,
    f7: (t: T7) => T8,
    f8: (t: T8) => T9
): T9

export function nilPipe<T1, T2, T3, T4, T5, T6, T7, T8, T9, T10>(
    value: T1,
    f1: (t: T1) => T2,
    f2: (t: T2) => T3,
    f3: (t: T3) => T4,
    f4: (t: T4) => T5,
    f5: (t: T5) => T6,
    f6: (t: T6) => T7,
    f7: (t: T7) => T8,
    f8: (t: T8) => T9,
    f9: (t: T9) => T10
): T10

export function nilPipe<T1, T2, T3, T4, T5, T6, T7, T8, T9, T10, T11>(
    value: T1,
    f1: (t: T1) => T2,
    f2: (t: T2) => T3,
    f3: (t: T3) => T4,
    f4: (t: T4) => T5,
    f5: (t: T5) => T6,
    f6: (t: T6) => T7,
    f7: (t: T7) => T8,
    f8: (t: T8) => T9,
    f9: (t: T9) => T10,
    f10: (t: T10) => T11
): T11

export function nilPipe(
    value: unknown,
    f1: (t: unknown) => unknown = identity,
    f2: (t: unknown) => unknown = identity,
    f3: (t: unknown) => unknown = identity,
    f4: (t: unknown) => unknown = identity,
    f5: (t: unknown) => unknown = identity,
    f6: (t: unknown) => unknown = identity,
    f7: (t: unknown) => unknown = identity,
    f8: (t: unknown) => unknown = identity,
    f9: (t: unknown) => unknown = identity,
    f10: (t: unknown) => unknown = identity
) {
    for (const f of [f1, f2, f3, f4, f5, f6, f7, f8, f9, f10]) {
        if (isNil(value)) return value
        value = f(value)
    }

    return value
}
